import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartCount extends Component {
  requiredRefs = ["cartBubbleCount"];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? "0", 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : "99+";
  }

  get inDrawer() {
    return this.dataset.context === "drawer";
  }

  /**
   * KS v3.9.156 Optimistic UI: 담기 클릭 직후 서버 응답 전에 먼저 더한 수량.
   * 서버의 CartAddEvent(증분)가 도착하면 이 값을 빼서 이중 가산을 막고,
   * 절대값 CartUpdateEvent 는 서버 진실이므로 그대로 덮어쓴다.
   * @type {number}
   */
  #pendingDelta = 0;

  /**
   * 서버 응답을 기다리지 않고 뱃지를 먼저 올린다. 드로어 안 뱃지는 서버 렌더가
   * 정본이라 건드리지 않는다.
   * @param {number} delta
   */
  applyOptimisticDelta(delta) {
    if (this.inDrawer || !Number.isFinite(delta) || delta <= 0) return;
    this.#pendingDelta += delta;
    this.renderCartBubble(this.currentCartCount + delta);
  }

  /**
   * 요청이 실패했을 때 먼저 올린 만큼 되돌린다. 이미 서버 이벤트로 정산됐으면 no-op.
   */
  revertOptimistic() {
    if (this.#pendingDelta === 0) return;
    const next = Math.max(0, this.currentCartCount - this.#pendingDelta);
    this.#pendingDelta = 0;
    this.renderCartBubble(next);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event
   * Supports both incremental (CartAddEvent) and absolute (CartUpdateEvent) count updates
   * @param {Event} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const isIncremental = event.detail.data?.isIncremental ?? false;

    if (isIncremental) {
      // Incremental update: add delta to current count
      let currentCount = parseInt(this.refs.cartBubbleCount.textContent ?? "0", 10);

      if (!this.inDrawer) {
        // 낙관적으로 먼저 더한 몫은 이미 화면에 있으므로 서버 증분에서 뺀다.
        currentCount += itemCount - this.#pendingDelta;
        this.#pendingDelta = 0;
      }

      this.renderCartBubble(Math.max(0, currentCount));
    } else {
      // Absolute update: set count directly from server
      this.#pendingDelta = 0;
      this.renderCartBubble(itemCount);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   */
  renderCartBubble = async (itemCount) => {
    this.currentCartCount = itemCount;

    this.classList.toggle("hidden", itemCount === 0);
    this.classList.toggle("cart-count--small-medium", itemCount > 99);
    document.body.classList.toggle("cart-has-items", itemCount > 0);
  };
}

if (!customElements.get("cart-count")) {
  customElements.define("cart-count", CartCount);
}
